import React, { useContext, useEffect, useRef, useState } from 'react';
import useClickOutside from '../hooks/useClickOutside';
import { APIContext } from '../contexts/APIContext';
import MultiSelectDropdown from './ui/MultiSelectDropdown';
import { useToast } from './ui/toast/UseToast';
import {
  createNewInterviewSecondary,
  fetchInterviewCountryDetails,
  fetchInterviewProductDetails,
  fetchInterviewProjectDetails,
  fetchInterviewVenueDetails,
  trackUserClicks,
  validateTranscriptInput,
  getValidToken,
} from '../utils';
import { useInputHandler } from '../hooks/useInputHandler';
import BounceLoader from './ui/Loaders/BounceLoader';

type ResearchType = 'Primary' | 'Secondary';

interface SecondaryProps {
  researchType: ResearchType;
  setResearchType: (type: ResearchType) => void;
}

type Project = {
  project_id: number;
  project_name: string;
};

type FormData = {
  selectedProject: string;
  accessDate: string;
  articleTitle: string;
  publication: string;
  publicationDate: string;
  articleURL: string;
  transcript: string;
  selectedCountries: string[];
  selectedVenues: string[];
  selectedProducts: string[];
};

const NewInterviewSecondary = ({ researchType, setResearchType }: SecondaryProps) => {
  const {
    websocketConnected,
    websocketConnectionRef: { current: websocket },
    interviewProjectNames,
    interviewCountryNames,
    interviewVenueNames,
    interviewProductNames,
    accounts,
    newInterviewStatus,
    setNewInterviewStatus,
    userDetails,
    chatSessionId,
    WBUId,
    AppName,
  } = useContext(APIContext);
  const [showOptionalInformation, setShowOptionalInformation] = useState<boolean>(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState<boolean>(false);
  const projectDropdownRef = useRef(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedVenues, setSelectedVenues] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [accessDate, setAccessDate] = useState('');
  const [publicationDate, setPublicationDate] = useState('');
  const [articleTitle, setArticleTitle] = useState('');
  const [publication, setPublication] = useState('');
  const [articleURL, setArticleURL] = useState('');
  const [transcript, setTranscript] = useState('');
  const [transcriptError, setTranscriptError] = useState('');
  const [reviewModeSecondary, setReviewModeSecondary] = useState(false);
  const [secondaryFormData, setSecondaryFormData] = useState<FormData>({
    selectedProject: '',
    accessDate: '',
    articleTitle: '',
    publication: '',
    publicationDate: '',
    articleURL: '',
    transcript: '',
    selectedCountries: [],
    selectedVenues: [],
    selectedProducts: [],
  });
  const [showLoader, setShowLoader] = useState(false);
  const { toast } = useToast();

  useClickOutside(projectDropdownRef, () => {
    setShowProjectDropdown(false);
  });

  useEffect(() => {
    if (!websocket || websocket.state !== 'Connected') return;
    (async () => {
      const token = await getValidToken();
      if (!interviewProjectNames.length) {
        fetchInterviewProjectDetails(accounts, websocket, token);
      }

      if (!interviewCountryNames.length) {
        fetchInterviewCountryDetails(accounts, websocket, token);
      }

      if (!interviewVenueNames.length) {
        fetchInterviewVenueDetails(accounts, websocket, token);
      }

      if (!interviewProductNames.length) {
        fetchInterviewProductDetails(accounts, websocket, token);
      }
    })();
  }, [websocket, websocketConnected]);

  useEffect(() => {
    if (!newInterviewStatus) return;

    if (newInterviewStatus === 'success') {
      toast({
        status: 'success',
        description: 'Transcript uploaded successfully',
      });

      setSelectedProject('');
      setProjectSearch('');
      setAccessDate('');
      setArticleTitle('');
      setPublication('');
      setPublicationDate('');
      setArticleURL('');
      setTranscript('');
      setSelectedCountries([]);
      setSelectedVenues([]);
      setSelectedProducts([]);

      scrollToTop();
      setReviewModeSecondary(false);
      setShowLoader(false);
    } else {
      toast({
        status: 'info',
        description: 'Failed to upload Transcript',
      });

      setShowLoader(false);
    }

    setNewInterviewStatus('');
  }, [newInterviewStatus]);

  useEffect(() => {
    if (reviewModeSecondary) {
      const areas = document.querySelectorAll('.-reviewmode textarea');

      areas.forEach((t: any) => {
        // set initial height
        t.style.height = 'auto';
        t.style.height = t.scrollHeight + 'px';
        t.style.overflow = 'hidden';
      });
    }
    return () => {
      const areas = document.querySelectorAll('.expand-class textarea');
      // runs when component unmounts or before next effect
      areas.forEach((t: any) => {
        t.style.height = '18.25rem';
        t.style.overflow = '';
      });
    };
  }, [reviewModeSecondary]);
  const filteredProjects =
    interviewProjectNames &&
    interviewProjectNames.filter((p: Project) => p.project_name.toLowerCase().includes(projectSearch.toLowerCase()));

  const handleSelectProject = (name: string) => {
    // setReviewModeSecondary(true);
    setSelectedProject(name);
    setProjectSearch(name);
    setShowProjectDropdown(false);
  };

  const handleDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const value = e.target.value;
    // setReviewModeSecondary(true);

    if (!value) {
      setter('');
      return;
    }

    setter(value);
  };

  const textRegex = /^[a-zA-Z0-9 ._-]*$/;
  const urlRegex = /^[A-Za-z0-9.\-_/:]*$/;

  const articleTitleHandlers = useInputHandler(textRegex, setArticleTitle, setReviewModeSecondary);
  const publicationHandlers = useInputHandler(textRegex, setPublication, setReviewModeSecondary);
  const articleURLHandlers = useInputHandler(urlRegex, setArticleURL, setReviewModeSecondary);

  const handleTranscriptInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    // setReviewModeSecondary(true);

    setTranscript(newValue);

    if (validateTranscriptInput(newValue)) {
      setTranscriptError(`Html tags are not allowed`);
    } else {
      setTranscriptError('');
    }
  };

  const handleTranscriptBlur = () => {
    const trimmed = transcript.trim();

    if (transcript !== trimmed) {
      setTranscript(trimmed);
    }

    if (validateTranscriptInput(trimmed)) {
      setTranscriptError(`Html tags are not allowed`);
    }
  };

  const handleReview = () => {
    setSecondaryFormData({
      selectedProject: selectedProject,
      accessDate: accessDate,
      articleTitle: articleTitle,
      publication: publication,
      publicationDate: publicationDate,
      articleURL: articleURL,
      transcript: transcript,
      selectedCountries: selectedCountries,
      selectedVenues: selectedVenues,
      selectedProducts: selectedProducts,
    });
    setReviewModeSecondary(true);
    scrollToTop();
  };

  const handleSubmit = async () => {
    const token = await getValidToken();
    setShowLoader(true);
    createNewInterviewSecondary(accounts, websocket, secondaryFormData, token);
    trackUserClicks(websocket, 'SecondarySubmit', userDetails, chatSessionId, WBUId, AppName, accounts, token);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <div className="c-tab-content">
        <div className="c-tab-content__content">
          <div className="c-tab-content__input-wrap">
            <label>Research Type*</label>
            <div className="c-tab-content__radio-btn">
              <label className="o-radio -rdo-className true">
                <input
                  type="radio"
                  name="primary1"
                  id="value1"
                  value="Primary"
                  checked={researchType === 'Primary'}
                  onChange={() => setResearchType('Primary')}
                />
                <label htmlFor="value1">Primary</label>
              </label>
              <label className="o-radio ">
                <input
                  type="radio"
                  name="primary1"
                  id="value2"
                  value="Secondary"
                  checked={researchType === 'Secondary'}
                  onChange={() => setResearchType('Secondary')}
                />
                <label htmlFor="value2">Secondary</label>
              </label>
            </div>
          </div>
          <div className="c-tab-content__input">
            <div className="c-tab-content__input-wrap">
              <label>Project*</label>
              <div className={`c-tab-content__input-text ${reviewModeSecondary ? '-reviewmode' : ''}`}>
                <div className="o-multi-select-two " tabIndex={0} ref={projectDropdownRef}>
                  <div className={`o-multi-select-two__head ${showProjectDropdown ? '-active' : ''}`}>
                    <div className="o-input-wrap ">
                      <i className="icon-search"></i>
                      <input
                        type="text"
                        className="o-input -dropdown"
                        id="input1"
                        name="input1"
                        placeholder="Search & Select Project..."
                        value={projectSearch}
                        onChange={(e) => {
                          // setReviewModeSecondary(true);
                          const value = e.target.value;
                          setProjectSearch(value);
                          setSelectedProject(value);
                        }}
                        onClick={() => setShowProjectDropdown(true)}
                      />
                    </div>
                    <i
                      className={`o-multi-select-two__arrow icon-caret ${showProjectDropdown ? '-active' : ''}`}
                      tabIndex={0}
                      onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                    ></i>
                  </div>
                  <div className={`o-multi-select-two__dropdown ${showProjectDropdown ? '-active' : ''}`}>
                    <ul className="o-multi-select-two__list">
                      {filteredProjects.length > 0 ? (
                        filteredProjects.map((p: Project) => (
                          <a
                            key={p.project_id}
                            role="option"
                            className="c-dropdown-item"
                            onClick={() => handleSelectProject(p.project_name)}
                            style={{ cursor: 'pointer' }}
                          >
                            {p.project_name}
                          </a>
                        ))
                      ) : (
                        <li className="c-dropdown-item disabled">No results found</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="c-tab-content__input-wrap">
              <label> Access Date*</label>
              <div className={`c-tab-content__input-date ${reviewModeSecondary ? '-reviewmode' : ''} `}>
                <div className="o-input-wrap ">
                  <input
                    type="date"
                    placeholder="mm/dd/yyyy"
                    onChange={(e) => handleDateChange(e, setAccessDate)}
                    className={`o-input ${accessDate === '' ? 'no-date' : ''}`}
                    value={accessDate}
                    style={{ paddingBottom: '10px' }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="c-tab-content__input">
            <div className="c-tab-content__input-wrap">
              <label>Article Title*</label>
              <div className={`o-input-wrap ${reviewModeSecondary ? '-reviewmode' : ''}`}>
                <input
                  type="text"
                  className="o-input"
                  id="input1"
                  name="input1"
                  placeholder="Add Article Title"
                  maxLength={250}
                  value={articleTitle}
                  {...articleTitleHandlers}
                />
              </div>{' '}
            </div>
            <div className="c-tab-content__input-wrap">
              <label>Publication*</label>
              <div className={`o-input-wrap ${reviewModeSecondary ? '-reviewmode' : ''}`}>
                <input
                  type="text"
                  className="o-input"
                  id="input1"
                  name="input1"
                  placeholder="Add Publication"
                  maxLength={250}
                  value={publication}
                  {...publicationHandlers}
                />
              </div>{' '}
            </div>
          </div>
          <div className="c-tab-content__input">
            <div className="c-tab-content__input-wrap">
              <label>Publication Date*</label>
              <div className={`c-tab-content__input-date ${reviewModeSecondary ? '-reviewmode' : ''} `}>
                <div className="o-input-wrap ">
                  <input
                    type="date"
                    placeholder="mm/dd/yyyy"
                    onChange={(e) => handleDateChange(e, setPublicationDate)}
                    className={`o-input ${publicationDate === '' ? 'no-date' : ''}`}
                    value={publicationDate}
                    style={{ paddingBottom: '10px' }}
                  />
                </div>
              </div>
            </div>

            <div className="c-tab-content__input-wrap">
              <label> Article URL*</label>
              <div className={`o-input-wrap ${reviewModeSecondary ? '-reviewmode' : ''}`}>
                <input
                  type="text"
                  className="o-input"
                  id="input1"
                  name="input1"
                  placeholder="Add article url here"
                  maxLength={250}
                  value={articleURL}
                  {...articleURLHandlers}
                />
              </div>
            </div>
          </div>
          <div className="c-tab-content__input">
            <div className="c-tab-content__input-wrap -textarea">
              <label>Interview Transcript*</label>
              <div className={`o-text-area expand-class ${reviewModeSecondary ? '-reviewmode' : ''}`}>
                <textarea
                  className="o-text-area__input"
                  placeholder="Write interview notes here"
                  maxLength={100000}
                  value={transcript}
                  // onKeyDown={handleKeyPress}
                  onChange={handleTranscriptInput}
                  onBlur={handleTranscriptBlur}
                  style={{ height: '18.25rem' }}
                ></textarea>
                <span className="o-text-area__total-count" style={{ marginTop: '-.9rem' }}>
                  {transcript.length.toLocaleString()} / 100,000
                </span>
                {transcriptError && (
                  <span className="o-text-area__error" style={{ bottom: '.1rem' }}>
                    {transcriptError}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="c-tab-content__input-expand">
            <a
              className="c-tab-content__accordion c-accordion__control"
              href="javascript:;"
              role="button"
              aria-expanded={showOptionalInformation}
              aria-controls="region2"
              onClick={() => setShowOptionalInformation(!showOptionalInformation)}
            >
              Optional Information
            </a>
            <div
              id="region2"
              className={`c-accordion__collapse ${showOptionalInformation ? '-show' : ''}`}
              aria-hidden={!showOptionalInformation}
              data-parentid="accordionParent"
            >
              <div className="c-tab-content__input">
                <MultiSelectDropdown
                  label="Country"
                  options={interviewCountryNames}
                  selected={selectedCountries}
                  setSelected={setSelectedCountries}
                  placeholder="Search & Select countries..."
                  review={reviewModeSecondary}
                  setReview={setReviewModeSecondary}
                />
                <MultiSelectDropdown
                  label="Venue"
                  options={interviewVenueNames}
                  selected={selectedVenues}
                  setSelected={setSelectedVenues}
                  placeholder="Search & Select venues..."
                  review={reviewModeSecondary}
                  setReview={setReviewModeSecondary}
                />
              </div>

              <div className="c-tab-content__input">
                <MultiSelectDropdown
                  label="Product"
                  options={interviewProductNames}
                  selected={selectedProducts}
                  setSelected={setSelectedProducts}
                  placeholder="Search & Select products..."
                  review={reviewModeSecondary}
                  setReview={setReviewModeSecondary}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {!reviewModeSecondary ? (
        <div className="c-stickyfooter__wrap  -review" style={{ position: 'fixed', zIndex: 2 }}>
          <div className="app-container c-stickyfooter__inner">
            <div className="c-stickyfooter__right">
              <button
                type="button"
                className="o-primary-button "
                aria-label="Submit"
                disabled={
                  selectedProject &&
                  accessDate &&
                  articleTitle &&
                  publication &&
                  publicationDate &&
                  articleURL &&
                  transcript &&
                  !transcriptError
                    ? false
                    : true
                }
                onClick={handleReview}
              >
                Review
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="c-stickyfooter__wrap  -review" style={{ position: 'fixed', zIndex: 2 }}>
          <div className="app-container c-stickyfooter__inner">
            <div className="c-stickyfooter__right">
              <button
                type="button"
                className="o-secondary-button "
                aria-label="Edit"
                onClick={() => setReviewModeSecondary(false)}
              >
                Edit
              </button>
              <button type="button" className="o-primary-button " aria-label="Submit" onClick={handleSubmit}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
      {showLoader && (
        <div className="fixed inset-0 flex flex-col justify-center items-center bg-[#f1f1f180] backdrop-blur-[1px] z-[9999]">
          <BounceLoader loading={true} size={60} />
          <p className="mt-6 text-lg">One Moment Please...</p>
        </div>
      )}
    </>
  );
};

export default NewInterviewSecondary;
